import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { httpClient } from '../http-client';

// --- Query Key Factory ---
export const adminOpsKeys = {
  all: ['admin-ops'] as const,
  organizations: () => [...adminOpsKeys.all, 'organizations'] as const,
  // When called without filters, returns a 2-element prefix key that matches
  // ALL user queries via React Query's partial prefix matching.
  // With filters, returns a 3-element key for a specific query.
  users: (filters?: AdminUsersFilters) =>
    filters
      ? ([...adminOpsKeys.all, 'users', filters] as const)
      : ([...adminOpsKeys.all, 'users'] as const),
  user: (userId: string) => [...adminOpsKeys.all, 'user', userId] as const,
  roles: () => [...adminOpsKeys.all, 'roles'] as const,
  roleCounts: (orgId?: string) =>
    orgId
      ? ([...adminOpsKeys.all, 'role-counts', orgId] as const)
      : ([...adminOpsKeys.all, 'role-counts'] as const),
  // Analytics key only includes filters when filters are present, so an
  // unfiltered invalidation prefix matches every analytics query. Mirrors
  // the conditional-include pattern used by `users:` above (per the
  // partialDeepEqual gotcha — `undefined` vs object breaks prefix match).
  userAnalytics: (filters?: UserAnalyticsFilters) =>
    filters
      ? ([...adminOpsKeys.all, 'user-analytics', filters] as const)
      : ([...adminOpsKeys.all, 'user-analytics'] as const),
  orgMembership: (userId: string, orgId: string) =>
    [...adminOpsKeys.all, 'org-membership', userId, orgId] as const,
  sessionStats: () => [...adminOpsKeys.all, 'session-stats'] as const,
};

// --- Types ---
export interface AdminOrganization {
  id: string;
  name: string;
  short_name?: string;
  member_count?: number;
  [key: string]: unknown;
}

export interface AdminUserRole {
  role_id: string;
  organization_id: string | null;
  role: {
    id: string;
    role_name: string;
    role_type: string;
  };
}

export interface AdminUserOrgMembership {
  organization_id: string;
  is_active: boolean;
  membership_status: string;
  member_number: string | null;
  organization: {
    id: string;
    name: string;
    short_name: string;
  };
}

export interface AdminUser {
  id: string;
  auth_user_id: string;
  primary_email: string;
  first_name?: string;
  last_name?: string;
  display_first_name?: string;
  display_last_name?: string;
  phone?: string;
  gender?: string;
  date_of_birth?: string;
  address1?: string;
  address2?: string;
  city?: string;
  state?: string;
  zip?: string;
  roles: AdminUserRole[];
  organization_memberships: AdminUserOrgMembership[];
  created_at?: string;
  // Account-level activation. Defaults to true. When false the account has
  // been soft-deactivated by an admin — the user can still authenticate
  // but their login flow routes them to the deactivated landing page.
  is_active?: boolean;
  deactivated_at?: string | null;
  deactivated_by_user_id?: string | null;
  deactivation_reason?: string | null;
  [key: string]: unknown;
}

export interface AdminUsersFilters {
  page?: number;
  pageSize?: number;
  search?: string;
  roleId?: string;
  // Multi-role UNION filter — pass an array when the user selects 2+ roles.
  // The hook serializes as a comma-separated `roleIds=a,b,c` query param.
  // Backend ignores `roleId` when `roleIds` is set.
  roleIds?: string[];
  orgId?: string;
  sortBy?: 'name' | 'created_at' | 'email';
  sortOrder?: 'asc' | 'desc';
  // Account-deactivation filter. Backend defaults to 'active' (hides
  // soft-deactivated users from the default Manage Users view). The
  // Deactivated chip sends 'deactivated' to show ONLY those accounts.
  activationStatus?: 'active' | 'deactivated' | 'all';
}

export interface AdminRole {
  id: string;
  role_name: string;
  role_type: string;
  description?: string;
  [key: string]: unknown;
}

export interface RoleCount {
  role_name: string;
  count: number;
}

// Filters accepted by the analytics endpoint — same shape as the user-list
// filters minus pagination + sort (we aggregate the full filtered set).
export interface UserAnalyticsFilters {
  orgId?: string;
  roleId?: string;
  roleIds?: string[];
  search?: string;
  activationStatus?: 'active' | 'deactivated' | 'all';
}

export interface UserAnalytics {
  total: number;
  gender: {
    male: number;
    female: number;
    unknown: number;
  };
  age: {
    '0-17': number;
    '18-30': number;
    '31-45': number;
    '46-60': number;
    '60+': number;
    unknown: number;
  };
  memberStatus: {
    active: number;
    inactive: number;
    none: number;
  };
  signups: {
    daily: Array<{ date: string; count: number }>;
    total: number;
    windowDays: number;
  };
}

interface AddUserRoleData {
  roleId: string;
  organizationId?: string;
}

interface AddOrgMembershipData {
  organizationId: string;
  memberNumber?: string;
}

interface OrgMembership {
  id: string;
  user_profile_id: string;
  organization_id: string;
  membership_status: string;
  is_active: boolean;
  [key: string]: unknown;
}

interface UpdateMembershipStatusData {
  status: string;
}

interface SessionStats {
  total: number;
  active: number;
  expired: number;
  [key: string]: unknown;
}

interface CleanupResult {
  cleaned: number;
}

// --- Query Hooks ---
export function useAdminOrganizations() {
  return useQuery({
    queryKey: adminOpsKeys.organizations(),
    queryFn: async () => {
      const res = await httpClient.get<{ orgs: AdminOrganization[]; count: number }>(
        '/admin-ops/organizations',
      );
      return res;
    },
  });
}

export function useAdminUserDetail(userId: string | null | undefined) {
  return useQuery({
    queryKey: adminOpsKeys.user(userId ?? ''),
    enabled: !!userId,
    queryFn: () =>
      httpClient.get<{ user: AdminUser }>(`/admin-ops/users/${userId}`),
  });
}

export function useAdminUsersList(filters?: AdminUsersFilters) {
  const qs = new URLSearchParams();
  if (filters?.page) qs.set('page', String(filters.page));
  if (filters?.pageSize) qs.set('pageSize', String(filters.pageSize));
  if (filters?.search) qs.set('search', filters.search);
  // Multi-role UNION filter wins over single-role when both present —
  // matches the backend precedence rule.
  if (filters?.roleIds && filters.roleIds.length > 0) {
    qs.set('roleIds', filters.roleIds.join(','));
  } else if (filters?.roleId) {
    qs.set('roleId', filters.roleId);
  }
  if (filters?.orgId) qs.set('orgId', filters.orgId);
  if (filters?.sortBy) qs.set('sortBy', filters.sortBy);
  if (filters?.sortOrder) qs.set('sortOrder', filters.sortOrder);
  if (filters?.activationStatus) qs.set('activationStatus', filters.activationStatus);
  const query = qs.toString() ? `?${qs}` : '';
  return useQuery({
    queryKey: adminOpsKeys.users(filters),
    queryFn: () =>
      httpClient.get<{
        users: AdminUser[];
        total: number;
        page: number;
        pageSize: number;
        hasMore: boolean;
      }>(`/admin-ops/users${query}`),
    placeholderData: keepPreviousData,
  });
}

export function useAdminRolesList() {
  return useQuery({
    queryKey: adminOpsKeys.roles(),
    queryFn: async () => {
      const res = await httpClient.get<{ roles: AdminRole[] }>('/admin-ops/roles');
      return res;
    },
  });
}

export function useRoleCounts(
  orgId?: string,
  activationStatus?: 'active' | 'deactivated' | 'all',
) {
  const qs = new URLSearchParams();
  if (orgId) qs.set('orgId', orgId);
  if (activationStatus) qs.set('activationStatus', activationStatus);
  const query = qs.toString() ? `?${qs}` : '';
  return useQuery({
    // Cache key must include activationStatus or React Query serves a
    // stale "active" response when the admin toggles Deactivated on.
    queryKey: [...adminOpsKeys.roleCounts(orgId), activationStatus ?? 'active'] as const,
    queryFn: async () => {
      const res = await httpClient.get<{ counts: RoleCount[] }>(
        `/admin-ops/role-counts${query}`,
      );
      return res;
    },
  });
}

/**
 * Aggregate gender + age-bucket counts for the SAME filtered user set the
 * users-list endpoint would return for the same filters. Backs the analytics
 * cards on Manage Users — those have to reflect the full filtered population,
 * not just the visible page, so the aggregation runs server-side.
 *
 * `placeholderData` keeps the previous response on screen while a re-filter
 * is in flight, so the cards don't flash to a "0 of 0" empty state on every
 * keystroke in the search box.
 */
export function useUserAnalytics(filters?: UserAnalyticsFilters) {
  const qs = new URLSearchParams();
  if (filters?.orgId) qs.set('orgId', filters.orgId);
  if (filters?.roleIds && filters.roleIds.length > 0) {
    qs.set('roleIds', filters.roleIds.join(','));
  } else if (filters?.roleId) {
    qs.set('roleId', filters.roleId);
  }
  if (filters?.search) qs.set('search', filters.search);
  if (filters?.activationStatus) qs.set('activationStatus', filters.activationStatus);
  const query = qs.toString() ? `?${qs}` : '';
  return useQuery({
    queryKey: adminOpsKeys.userAnalytics(filters),
    queryFn: () => httpClient.get<UserAnalytics>(`/admin-ops/user-analytics${query}`),
    placeholderData: keepPreviousData,
  });
}

export function useGetOrgMembership(userId: string, orgId: string) {
  return useQuery({
    queryKey: adminOpsKeys.orgMembership(userId, orgId),
    queryFn: () =>
      httpClient.get<OrgMembership>(
        `/admin-ops/users/${userId}/org-membership/${orgId}`,
      ),
    enabled: !!userId && !!orgId,
  });
}

export function useSessionStats() {
  return useQuery({
    queryKey: adminOpsKeys.sessionStats(),
    queryFn: () =>
      httpClient.get<SessionStats>('/admin-ops/security/sessions'),
  });
}

// --- Mutation Hooks ---
export function useAddUserRole() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      userId,
      ...data
    }: AddUserRoleData & { userId: string }) =>
      httpClient.post(`/admin-ops/users/${userId}/roles`, data),
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: adminOpsKeys.users() });
      qc.invalidateQueries({ queryKey: adminOpsKeys.roleCounts() });
      qc.invalidateQueries({ queryKey: adminOpsKeys.user(vars.userId) });
    },
  });
}

export function useRemoveUserRole() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, roleId, organizationId }: { userId: string; roleId: string; organizationId?: string | null }) => {
      const qs = organizationId ? `?organizationId=${organizationId}` : '';
      return httpClient.delete(`/admin-ops/users/${userId}/roles/${roleId}${qs}`);
    },
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: adminOpsKeys.users() });
      qc.invalidateQueries({ queryKey: adminOpsKeys.roleCounts() });
      qc.invalidateQueries({ queryKey: adminOpsKeys.user(vars.userId) });
    },
  });
}

/**
 * Delete an organization. System (ALL-org) admin only; `confirmName` must
 * match the org's exact name; the ALL sentinel org can never be deleted —
 * all enforced server-side too. Fails (409) if the org still has rodeos or
 * other dependent records.
 */
export function useDeleteOrganization() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ organizationId, confirmName }: { organizationId: string; confirmName: string }) =>
      httpClient.delete<{ success: boolean; deleted: true; organizationId: string; name: string }>(
        `/admin-ops/organizations/${organizationId}`,
        { confirm_name: confirmName },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: adminOpsKeys.organizations() });
      qc.invalidateQueries({ queryKey: adminOpsKeys.users() });
      qc.invalidateQueries({ queryKey: adminOpsKeys.roleCounts() });
      // The rodeo/jackpot org dropdowns are keyed under jackpotOps — broadest
      // safe invalidation: drop everything (cheap, infrequent action).
      qc.invalidateQueries();
    },
  });
}

/**
 * Set a user's SYSTEM (ALL-org) role — i.e. grant/revoke super-admin.
 * Hits PUT /api/auth/admin/users/:userId/role, which replaces the user's
 * ALL-org role and revokes their sessions (they re-login with the new role).
 * Caller-only allowed for ALL-org admins; can't target yourself (enforced
 * server-side too). `userId` is the user_profiles.id.
 */
export function useSetUserSystemRole() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, roleId }: { userId: string; roleId: string }) =>
      httpClient.put<{ success: boolean; message: string }>(
        `/auth/admin/users/${userId}/role`,
        { role_id: roleId },
      ),
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: adminOpsKeys.users() });
      qc.invalidateQueries({ queryKey: adminOpsKeys.roleCounts() });
      qc.invalidateQueries({ queryKey: adminOpsKeys.user(vars.userId) });
    },
  });
}

export function useAddOrgMembership() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      userId,
      ...data
    }: AddOrgMembershipData & { userId: string }) =>
      httpClient.post<OrgMembership>(
        `/admin-ops/users/${userId}/org-membership`,
        data,
      ),
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: adminOpsKeys.users() });
      qc.invalidateQueries({ queryKey: adminOpsKeys.user(vars.userId) });
    },
  });
}

export function useUpdateMembershipStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      userId,
      orgId,
      ...data
    }: UpdateMembershipStatusData & { userId: string; orgId: string }) =>
      httpClient.request(
        `/admin-ops/users/${userId}/org-membership/${orgId}/status`,
        { method: 'PATCH', body: JSON.stringify(data) },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: adminOpsKeys.all });
    },
  });
}

export function useToggleMembershipActive() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      userId,
      orgId,
    }: { userId: string; orgId: string }) =>
      httpClient.request(
        `/admin-ops/users/${userId}/org-membership/${orgId}/active`,
        { method: 'PATCH' },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: adminOpsKeys.all });
    },
  });
}

export function useUpdateMemberNumber() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      userId,
      orgId,
      memberNumber,
    }: {
      userId: string;
      orgId: string;
      memberNumber: string | null;
    }) =>
      httpClient.request(
        `/admin-ops/users/${userId}/org-membership/${orgId}/member-number`,
        { method: 'PATCH', body: JSON.stringify({ memberNumber }) },
      ),
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: adminOpsKeys.users() });
      qc.invalidateQueries({ queryKey: adminOpsKeys.user(vars.userId) });
    },
  });
}

/**
 * Replace the user's single role for an organization (or clear it by
 * passing `roleId: null`). The roles & permissions modal treats each org
 * as a one-role slot — there is no "add multiple roles" surface anymore,
 * just "change to this role" or "clear it".
 *
 * Backed by PUT /admin-ops/users/:userId/org-membership/:orgId/role, which
 * deletes any existing user_roles for that (user, org) pair and inserts
 * the new one inside a service-layer rollback wrapper.
 */
export function useSetOrgRole() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      userId,
      orgId,
      roleId,
    }: { userId: string; orgId: string; roleId: string | null }) =>
      httpClient.request<{ success: true; role_id: string | null }>(
        `/admin-ops/users/${userId}/org-membership/${orgId}/role`,
        { method: 'PUT', body: JSON.stringify({ roleId }) },
      ),
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: adminOpsKeys.users() });
      qc.invalidateQueries({ queryKey: adminOpsKeys.user(vars.userId) });
      qc.invalidateQueries({ queryKey: adminOpsKeys.roleCounts() });
    },
  });
}

export function useCleanupSessions() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      httpClient.post<CleanupResult>('/admin-ops/security/cleanup'),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: adminOpsKeys.sessionStats() });
    },
  });
}

/**
 * Editable fields on the admin user-profile PUT. Mirrors the columns the
 * user-facing profile form writes — minus `primary_email` which is also
 * admin-editable here. All optional: admins are allowed to update any single
 * field and leave the rest untouched (no user-form required-field
 * enforcement). `null` is a valid value for nullable columns so admins can
 * actively clear a field rather than just blank-string it.
 */
export interface AdminUserProfileUpdate {
  first_name?: string | null;
  last_name?: string | null;
  display_first_name?: string | null;
  display_last_name?: string | null;
  primary_email?: string | null;
  phone?: string | null;
  address1?: string | null;
  address2?: string | null;
  city?: string | null;
  state?: string | null;
  zip?: string | null;
  date_of_birth?: string | null;
  gender?: string | null;
}

export function useAdminUpdateUserProfile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      userId,
      data,
    }: {
      userId: string;
      data: AdminUserProfileUpdate;
    }) =>
      httpClient.request(`/user-profiles/${userId}`, {
        method: 'PUT',
        body: JSON.stringify(data),
      }),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: adminOpsKeys.users() });
      // Also refresh the single-user view used by UserDetailsPage so
      // edits made there appear immediately without a manual reload.
      qc.invalidateQueries({ queryKey: adminOpsKeys.user(vars.userId) });
    },
  });
}

interface AdminResetPasswordResponse {
  success: boolean;
  message: string;
  // True when this reset also confirmed a previously-unconfirmed email.
  emailWasConfirmed?: boolean;
}

/**
 * Auth providers linked to a user (e.g. ['google'], ['email'], ['google','email']).
 * Lets the reset-password modal show whether it's a Google account.
 */
export function useAdminUserAuthProviders(userId: string | null | undefined) {
  return useQuery({
    queryKey: [...adminOpsKeys.user(userId ?? ''), 'auth-providers'] as const,
    queryFn: () => httpClient.get<{ providers: string[] }>(`/admin-ops/users/${userId}/auth-providers`),
    enabled: !!userId,
    staleTime: 5 * 60 * 1000,
  });
}

// Soft-deactivation toggle for the admin User Details page header. Backed by
// PATCH /admin-ops/users/:userId/activation — backend revokes sessions on
// deactivation, blocks the actor from toggling their own account, and
// returns the updated user_profiles row so we can patch the local cache.
export function useSetUserActivation() {
  const qc = useQueryClient();
  return useMutation({
    // `reason` is only honored when deactivating; the backend wipes any
    // prior reason on reactivation regardless of what's posted.
    mutationFn: ({
      userId,
      isActive,
      reason,
    }: {
      userId: string;
      isActive: boolean;
      reason?: string | null;
    }) =>
      httpClient.patch<{ success: boolean; user: AdminUser }>(
        `/admin-ops/users/${userId}/activation`,
        { isActive, reason: reason ?? null },
      ),
    onSuccess: (_data, vars) => {
      // The list view shows the deactivated badge too, so invalidate both.
      qc.invalidateQueries({ queryKey: adminOpsKeys.users() });
      qc.invalidateQueries({ queryKey: adminOpsKeys.user(vars.userId) });
    },
  });
}

export function useAdminResetPassword() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, newPassword }: { userId: string; newPassword: string }) =>
      httpClient.post<AdminResetPasswordResponse>(
        `/admin-ops/users/${userId}/reset-password`,
        { newPassword },
      ),
    onSuccess: (_data, vars) => {
      // Email-confirmation state may have changed — refresh the user views.
      qc.invalidateQueries({ queryKey: adminOpsKeys.users() });
      qc.invalidateQueries({ queryKey: adminOpsKeys.user(vars.userId) });
    },
  });
}

// ─── Impersonation ────────────────────────────────────────────────────────
//
// Backend swaps the session_id cookie when these mutations succeed, so the
// browser's identity flips on the next /auth/session call. After mutating,
// the consumer should:
//   1. Reset the React Query cache (so queries that captured the old user's
//      data don't bleed into the impersonated session — same defense
//      AuthContext applies on login/logout).
//   2. Re-fetch the auth session so the store + ImpersonationBanner update.
//   3. Navigate to /dashboard or wherever makes sense.
// We don't bake those steps into the hook — the caller owns navigation and
// has access to AuthContext + queryClient already.

interface ImpersonateResponse {
  success: boolean;
  impersonatedUserId: string;
  targetName: string;
}

export function useImpersonateUser() {
  return useMutation({
    mutationFn: (userId: string) =>
      httpClient.post<ImpersonateResponse>(
        `/admin-ops/users/${userId}/impersonate`,
        {},
      ),
  });
}

export function useStopImpersonating() {
  return useMutation({
    mutationFn: () =>
      httpClient.post<{ success: boolean }>('/admin-ops/impersonation/stop', {}),
  });
}
